import { useCallback, useRef, useState } from 'preact/hooks';

import { useId } from '../hooks/UNSAFE_useId';
import { DirectionalCollapseArrowIcon } from '../PRIVATE_ThemedIcons/DirectionalCollapseArrowIcon';
import { DirectionalExpandArrowIcon } from '../PRIVATE_ThemedIcons/DirectionalExpandArrowIcon';

import { BaseMenuItem } from './MenuItem';
import { useMenuContainerContext } from './useMenuContainerContext';
import { SubmenuProps } from './Submenu';
import { createPortal } from 'preact/compat';
import { MenuContainer } from './MenuContainer';
import { useMenuContext } from './useMenuContext';

export const SheetSubmenu = ({ label, children, startIcon, itemKey }: SubmenuProps) => {
  const { isMobile } = useMenuContext();
  const submenuId = useId();
  const submenuItemId = useId();
  const submenuTitleId = useId();

  const submenuMenuItemRef = useRef<HTMLAnchorElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [initialFocus, setInitialFocus] = useState<'menu' | 'secondItem' | 'secondItemDueToTouch'>(
    'menu'
  );

  const { menuContainerRef, changeKey } = useMenuContainerContext();

  const openSubmenuDirectional = useCallback(() => {
    openSubmenu();
    setInitialFocus('secondItem');
  }, []);

  const openSubmenuAction = useCallback((e: Event) => {
    openSubmenu();
    //If the submenu is open using keyboard focus has to go to second item(first is the title), if not to the menu Container
    if (e.type === 'keyup') {
      setInitialFocus('secondItem');
    } else {
      if (isMobile) {
        setInitialFocus('secondItemDueToTouch');
      } else {
        setInitialFocus('menu');
      }
    }
  }, []);

  const returnFocusToSubmenuItem = useCallback(() => {
    changeKey({ key: submenuItemId, reason: 'keyboard' });
    submenuMenuItemRef.current?.focus();
  }, [changeKey, submenuItemId, submenuMenuItemRef]);

  /* No need to "save" these functions with useCallback */

  const openSubmenu = () => {
    /* Menu hidden instead of unmounted for two main reasons:
      1. Children need to be updated even if menu is not present.
      2. We can't unmount parent since all the submenus are children of main menu */
    (menuContainerRef.current as HTMLElement).style.display = 'none';
    setIsOpen(true);
  };
  const closeSubmenu = () => {
    setIsOpen(false);
    if (menuContainerRef.current) {
      //Submenu is unmounted and prev menu is visible again.
      /*Dom manipulation is kind of dirty. Have to check for new approaches. JET-57239 */
      menuContainerRef.current.style.display = 'block';
    }
  };

  const closeSubmenuPressingTitleItem = (e: Event) => {
    closeSubmenu();
    //If the submenu is closed using keyboard focus has to go to submenuItem that opened submenu, if not to the menu Container
    if (e.type === 'keyup') {
      returnFocusToSubmenuItem();
    } else {
      if (isMobile) {
        changeKey({ key: submenuItemId, reason: 'touch' });
        // We need to add a little delay due to VO double tap producing focusing wrong item. Will be removed once animation is added for sheet submenus
        setTimeout(() => {
          submenuMenuItemRef.current?.focus();
        }, 0);
      } else {
        changeKey({ key: undefined, reason: 'click' });
        menuContainerRef.current?.focus();
      }
    }
  };

  const closeDirectional = () => {
    closeSubmenu();
    returnFocusToSubmenuItem();
  };

  return (
    <>
      <BaseMenuItem
        itemKey={itemKey}
        startIcon={startIcon}
        id={submenuItemId}
        submenuId={submenuId}
        ref={submenuMenuItemRef}
        label={label}
        endIcon={<DirectionalExpandArrowIcon></DirectionalExpandArrowIcon>}
        type="submenu"
        role="menuitem"
        onAction={openSubmenuAction}
        openSubmenu={openSubmenuDirectional}
        isSubmenuOpen={isOpen}></BaseMenuItem>
      {!isOpen
        ? null
        : // We need to use portal to ensure all submenus are siblings so if one dissapear not every submenu dissapear
          createPortal(
            <MenuContainer
              display="sheet"
              aria-label={label}
              menuId={submenuId}
              closeSubmenu={closeDirectional}
              menuLevel="sub"
              anchorRef={submenuMenuItemRef}
              //TODO: We need to check if this initial focus is desired based on some W3C specs
              currentFocus={initialFocus}>
              <BaseMenuItem
                id={submenuTitleId}
                menuItemLabelWeight="bold"
                role="menuitem"
                type="submenu"
                startIcon={<DirectionalCollapseArrowIcon></DirectionalCollapseArrowIcon>}
                onAction={closeSubmenuPressingTitleItem}
                label={label}></BaseMenuItem>
              {children}
            </MenuContainer>,
            menuContainerRef.current?.parentElement as HTMLElement
          )}
    </>
  );
};
